import { Route, Routes, Navigate, useLocation, useSearchParams } from 'react-router-dom';
// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Logout from './pages/Logout';
import ForgotPassword from './pages/ForgotPassword';
import ErrorPage from './pages/Error';

import UserView from './pages/User';
import UserDashboard, { userPermissions } from './pages/UserDashboard';
import UserGrids from './pages/UserGrids';
import UserSop from './pages/UserSop';
import UserAddresses from './pages/UserAddresses';
import UserOrders from './pages/UserOrders';
import UserCarts from './pages/UserCarts';
import CartView from './pages/CartView';
import OrderView from './pages/OrderView';
import RegisterUser from './pages/RegisterUser';
import Checkout from './pages/Checkout';

import UserList from './pages/UserList';

import OrgView from './pages/OrgView';
import OrgWrapper from './pages/OrgWrapper';

import SopList from './pages/SopList';

import Search from './pages/Search';

import CategoryList from './pages/CategoryList';
import CategoryView from './pages/CategoryView';
import HomepageView from './pages/HomepageView';
import PublishView from './pages/Publish';
import auth from './utils/auth.js';
import { useDispatch } from 'react-redux';
import AppLayout from './components/AppLayout';
import { sessionDestroy } from './slices/sessionSlice.js';
import { useSelector } from 'react-redux';

const EditPageWrapper = (props) => {
    const [queryParams] = useSearchParams();
    const url = queryParams.get('url');
    if (url === '/shop-homepage') {
        return <HomepageView {...props} />;
    } else {
        return <CategoryView {...props} />;
    }
};

const RequireAuth = ({ children }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    if (!auth.loggedIn()) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        auth.logout();
        dispatch(sessionDestroy());
        return <Navigate to="/login" state={{ nextPathname: location }} replace />;
    }

    return children;
};

const RequirePermissions = ({ requiredPermissions, children }) => {
    const session = useSelector((state) => state.session);
    const location = useLocation();
    const dispatch = useDispatch();
    if (!auth.loggedIn()) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        auth.logout();
        dispatch(sessionDestroy());
        return <Navigate to="/login" state={{ nextPathname: location }} replace />;
    }

    if (requiredPermissions && !session?.permissions?.includes(requiredPermissions)) {
        return <ErrorPage />;
    }

    return children;
};

const AppRoutes = () => {
    return (
        <Routes>
            <Route element={<AppLayout />}>
                <Route path="login" element={<Login />} />
                <Route path="forgot-password" element={<ForgotPassword />} />

                <Route
                    index
                    element={
                        <RequireAuth>
                            <Home />
                        </RequireAuth>
                    }
                />
                <Route
                    path="logout"
                    element={
                        <RequireAuth>
                            <Logout />
                        </RequireAuth>
                    }
                />

                <Route
                    path="search/:term"
                    element={
                        <RequireAuth>
                            <Search />
                        </RequireAuth>
                    }
                />

                <Route
                    path="register-user"
                    element={
                        <RequireAuth>
                            <RegisterUser />
                        </RequireAuth>
                    }
                />
                <Route
                    path="customers/:channel"
                    element={
                        <RequireAuth>
                            <UserList />
                        </RequireAuth>
                    }
                />
                <Route
                    path="org/:netsuite_entity"
                    element={
                        <RequireAuth>
                            <OrgWrapper />
                        </RequireAuth>
                    }
                >
                    <Route index element={<OrgView />} />
                    <Route path="grids" name="entity_grids" element={<UserGrids />} />
                </Route>
                <Route
                    path="sop/:freq_type"
                    element={
                        <RequireAuth>
                            <SopList />
                        </RequireAuth>
                    }
                />

                <Route
                    path="customer/:user_id"
                    element={
                        <RequireAuth>
                            <UserView />
                        </RequireAuth>
                    }
                >
                    <Route index element={<UserDashboard />} />
                    <Route path="grids" name="user_grids" element={<UserGrids />} />
                    <Route path="sop" element={<UserSop />} />
                    <Route path="addresses" element={<UserAddresses />} />
                    <Route path="orders">
                        <Route index element={<UserOrders />} />
                        <Route path=":order_number" element={<OrderView />} />
                    </Route>
                    <Route path="carts">
                        <Route index element={<UserCarts />} />
                        <Route path=":cart_id">
                            <Route index element={<CartView />} />
                            <Route path="checkout" element={<Checkout />} />
                        </Route>
                    </Route>
                </Route>

                <Route
                    path="orders/:order_number"
                    element={
                        <RequireAuth>
                            <OrderView />
                        </RequireAuth>
                    }
                />

                <Route
                    path="categories"
                    element={
                        <RequirePermissions requiredPermissions={userPermissions.CONTENT_ADMIN}>
                            <CategoryList />
                        </RequirePermissions>
                    }
                />

                <Route
                    path="category"
                    element={
                        <RequirePermissions requiredPermissions={userPermissions.CONTENT_ADMIN}>
                            <EditPageWrapper />
                        </RequirePermissions>
                    }
                />

                <Route
                    path="drafts"
                    element={
                        <RequireAuth>
                            <PublishView />
                        </RequireAuth>
                    }
                />
                <Route path="*" element={<ErrorPage />} />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
